import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KeyRound, BookOpen, Globe } from "lucide-react";
import { motion } from "framer-motion";
import {
  ComposableMap, Geographies, Geography,
} from "react-simple-maps";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import "@/i18n/fase6";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WORLD_TOPO = "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

export default function Estatisticas() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [geo, setGeo] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/stats/summary`).then((r) => r.json()).then(setSummary);
    fetch(`${BACKEND}/api/public/stats/geo`).then((r) => r.json()).then((d) => setGeo(d.countries || []));
    fetch(`${BACKEND}/api/public/stats/organizations`).then((r) => r.json()).then((d) => setOrgs(d.organizations || []));
  }, []);

  const maxCountryReq = Math.max(1, ...geo.map((g) => g.requests || 0));
  const geoByIso = new Map(geo.map((g) => [g.country_iso2, g]));
  const shade = (iso) => {
    const c = geoByIso.get(iso);
    if (!c) return "#F4E4C1";
    const ratio = Math.min(1, (c.requests || 0) / maxCountryReq);
    const alpha = 0.15 + ratio * 0.75;
    return `rgba(11, 76, 122, ${alpha})`;
  };

  const empty = !summary || summary.total_requests_30d === 0;

  return (
    <div className="min-h-screen bg-salnes-ivory" data-testid="page-estatisticas">
      <PublicHeader />
      <section className="relative pt-32 pb-16 border-b hairline">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(/brand/hero-salnes-alt2.jpg)", backgroundSize: "cover" }} />
        <div className="relative mx-auto max-w-[1200px] px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-3">Datos Abertos · Uso público</div>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-serif text-5xl lg:text-6xl text-salnes-ink">
            {t("fase6.stats_title")}
          </motion.h1>
          <p className="mt-4 text-lg text-salnes-ink/80 max-w-2xl">{t("fase6.stats_subtitle")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="mailto:datos@salnes.gal"><Button variant="outline" className="rounded-full" data-testid="stats-cta-key"><KeyRound size={14} className="mr-2" />{t("fase6.stats_cta_key")}</Button></a>
            <a href="/documentacion"><Button variant="outline" className="rounded-full"><BookOpen size={14} className="mr-2" />{t("fase6.stats_cta_docs")}</Button></a>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10 space-y-8">
          <div className="grid md:grid-cols-3 gap-4" data-testid="stats-kpis">
            <Kpi label={t("fase6.stats_kpi_requests")} value={summary?.total_requests_30d ?? "—"} />
            <Kpi label={t("fase6.stats_kpi_orgs")} value={summary?.unique_api_keys_active ?? "—"} />
            <Kpi label={t("fase6.stats_kpi_datasets")} value={summary?.top_datasets?.length ?? "—"} />
          </div>

          {empty ? (
            <div className="rounded-2xl border hairline bg-white p-12 text-center" data-testid="stats-empty">
              <Globe size={40} className="mx-auto text-salnes-stone opacity-40 mb-4" />
              <p className="text-salnes-stone italic max-w-lg mx-auto">{t("fase6.stats_empty")}</p>
            </div>
          ) : (
            <>
              <div className="rounded-2xl border hairline bg-white p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.stats_map_title")}</div>
                <div className="relative" style={{ maxHeight: 480 }} data-testid="stats-map">
                  <ComposableMap projectionConfig={{ scale: 145 }} width={1000} height={450} onMouseLeave={() => setTooltip(null)}>
                    <Geographies geography={WORLD_TOPO}>
                      {({ geographies }) => geographies.map((g) => {
                        const iso = g.properties.iso_a2 || g.properties.ISO_A2 || "";
                        const c = geoByIso.get(iso);
                        return (
                          <Geography
                            key={g.rsmKey} geography={g}
                            fill={shade(iso)}
                            stroke="#FBFAF7" strokeWidth={0.4}
                            style={{
                              default: { outline: "none" },
                              hover: { outline: "none", fill: "#0B4C7A", cursor: c ? "pointer" : "default" },
                              pressed: { outline: "none" },
                            }}
                            onMouseEnter={() => c && setTooltip({ name: c.country_name, req: c.requests, ips: c.unique_ips })}
                          />
                        );
                      })}
                    </Geographies>
                  </ComposableMap>
                  {tooltip && (
                    <div className="absolute top-2 right-2 rounded-lg bg-salnes-ink text-salnes-ivory px-3 py-2 text-xs pointer-events-none">
                      <div className="font-medium">{tooltip.name}</div>
                      <div>{tooltip.req} consultas · {tooltip.ips} IPs</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border hairline bg-white p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.stats_top_datasets")}</div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summary?.top_datasets || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" tick={{ fontSize: 10, fill: "#6B7280" }} />
                        <YAxis type="category" dataKey="code" tick={{ fontSize: 9, fill: "#6B7280" }} width={140} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                        <Bar dataKey="requests" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-2xl border hairline bg-white p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.stats_evolution")}</div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={summary?.daily_evolution || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#6B7280" }} />
                        <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                        <Line type="monotone" dataKey="count" stroke="#0B4C7A" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border hairline bg-white overflow-hidden">
                <div className="px-6 py-4 border-b hairline">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone">{t("fase6.stats_orgs_title")}</div>
                </div>
                <table className="w-full text-sm" data-testid="stats-orgs-table">
                  <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
                    <tr>
                      <th className="text-left px-4 py-3">{t("fase6.stats_th_org")}</th>
                      <th className="text-left px-4 py-3">{t("fase6.stats_th_country")}</th>
                      <th className="text-right px-4 py-3">{t("fase6.stats_th_requests")}</th>
                      <th className="text-left px-4 py-3">{t("fase6.stats_th_first_seen")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {orgs.map((o, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 font-mono text-[12px]">{o.organization_masked}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{o.country}</Badge></td>
                        <td className="px-4 py-3 text-right tabular-nums">{o.requests_last_30d}</td>
                        <td className="px-4 py-3 text-[12px] text-salnes-stone">{o.first_seen}</td>
                      </tr>
                    ))}
                    {orgs.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-8 text-center italic text-salnes-stone">—</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}

const Kpi = ({ label, value }) => (
  <div className="rounded-2xl border hairline bg-white p-6">
    <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">{label}</div>
    <div className="font-serif text-4xl text-salnes-atlantic tabular-nums mt-1">{value}</div>
  </div>
);
